import React, { useContext } from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import Footer from "../Components/Footer";
import Stats from "../Components/Stats";
import RecentAddedBooks from "../Components/RecentAddedBooks";
import PopularBooks from "../Components/PopularBooks";
import ReservedBooks from "../Components/ReservedBooks";
import { Badge, Button, Card } from "../ui";
import { AuthContext } from "../Context/AuthContext";
import LocalLibraryIcon from "@material-ui/icons/LocalLibrary";
import ExploreIcon from "@material-ui/icons/Explore";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";

const signatureHighlights = [
    {
        icon: <MenuBookIcon />,
        title: "Curated Knowledge",
        description:
            "Handpicked collections spanning research monographs, legendary fiction, and contemporary journals in a single immersive catalogue.",
    },
    {
        icon: <EventAvailableIcon />,
        title: "Intelligent Reservations",
        description:
            "Reserve, renew, and browse availability in real time with smart reminders tailored to your reading rhythm.",
    },
    {
        icon: <VerifiedUserIcon />,
        title: "Role-Aware Access",
        description:
            "Individual experiences for administrators, staff, and students powered by adaptive permissions and dashboards.",
    },
];

function Home() {
    const { user } = useContext(AuthContext);
    const insightsLink = user
        ? user.isAdmin
            ? "/dashboard@admin"
            : ["staff", "employee"].includes(user.userType)
                ? "/dashboard@staff"
                : "/dashboard@member"
        : "/signin";

    return (
        <div className="home">
            <section className="home__hero">
                <div className="home__hero-orbit" />
                <div className="home__hero-content">
                    <Badge variant="primary">Aurora Library Experience</Badge>
                    <h1>
                        A premium library ecosystem crafted for scholars, curators, and
                        visionaries.
                    </h1>
                    <p>
                        Discover luminous collections, orchestrate circulation seamlessly,
                        and personalize the ambiance of the entire platform with dynamic
                        theming. Every interaction has been tuned for graceful efficiency.
                    </p>
                    <div className="home__hero-actions">
                        <Button
                            as={Link}
                            to="/books"
                            variant="primary"
                            size="lg"
                            leadingIcon={<ExploreIcon />}
                        >
                            Browse Signature Collections
                        </Button>
                        <Button
                            as={Link}
                            to={user ? insightsLink : "/signin"}
                            variant="ghost"
                            size="lg"
                            leadingIcon={<LocalLibraryIcon />}
                        >
                            {user ? "Resume Session" : "Enter Your Portal"}
                        </Button>
                    </div>
                    <div className="home__hero-metrics">
                        <div>
                            <span className="home__metric-value">3.2k+</span>
                            <span className="home__metric-label">Volumes Digitised</span>
                        </div>
                        <div>
                            <span className="home__metric-value">98%</span>
                            <span className="home__metric-label">Reader Satisfaction</span>
                        </div>
                        <div>
                            <span className="home__metric-value">24/7</span>
                            <span className="home__metric-label">Intelligent Access</span>
                        </div>
                    </div>
                </div>
                <div className="home__hero-capsule">
                    <Card padding="md" className="home__hero-card" interactive>
                        <div className="home__hero-card-header">
                            <TrendingUpIcon />
                            <span>Live Insights</span>
                        </div>
                        <h3>Weekly Spotlight</h3>
                        <p>
                            Circulation is up <strong>18%</strong> this week as literature
                            festival releases arrive. Members are gravitating towards curated
                            study playlists and new media archives.
                        </p>
                        <Button as={Link} to={insightsLink} variant="secondary" leadingIcon={<TrendingUpIcon />}>
                            View dashboard intelligence
                        </Button>
                    </Card>
                </div>
            </section>

            <Stats />

            <section className="home__highlights">
                <div className="home__highlights-head">
                    <Badge variant="primary">Why institutions choose us</Badge>
                    <h2>Architected for premium library journeys</h2>
                    <p>
                        Every module inside the platform has been reimagined with elegant UI
                        patterns, adaptive theming, and performance telemetry. These pillars
                        power frictionless experiences.
                    </p>
                </div>
                <div className="home__highlights-grid">
                    {signatureHighlights.map((item) => (
                        <Card key={item.title} padding="md" interactive className="home__highlight-card">
                            <div className="home__highlight-icon">{item.icon}</div>
                            <h3>{item.title}</h3>
                            <p>{item.description}</p>
                        </Card>
                    ))}
                </div>
            </section>

            <section className="home__collections">
                <div className="home__section-heading">
                    <Badge variant="primary">Latest arrivals</Badge>
                    <h2>Freshly catalogued volumes</h2>
                </div>
                <RecentAddedBooks />
            </section>

            <section className="home__collections">
                <div className="home__section-heading">
                    <Badge variant="primary">Curated favourites</Badge>
                    <h2>Popular with our readers</h2>
                </div>
                <PopularBooks />
            </section>

            <section className="home__collections">
                <div className="home__section-heading">
                    <Badge variant="primary">Reserved today</Badge>
                    <h2>Currently in high demand</h2>
                </div>
                <ReservedBooks />
            </section>

            <Footer />
        </div>
    );
}

export default Home;
